import {
  formatAppDateTime,
  formatAppTime,
  toDateTimeLocalValue,
  parseToInstant
} from '../../../utils/appTimezone';

export const APPOINTMENT_STATUS = {
  EN_ATTENTE: { id: 'EN_ATTENTE', label: 'En attente', modifier: 'pending' },
  CONFIRME: { id: 'CONFIRME', label: 'Confirmé', modifier: 'confirmed' },
  TERMINE: { id: 'TERMINE', label: 'Terminé', modifier: 'done' },
  ANNULE: { id: 'ANNULE', label: 'Annulé', modifier: 'cancelled' },
  ABSENT: { id: 'ABSENT', label: 'Absent', modifier: 'absent' }
};

export function formatPatientLabel(item) {
  if (!item) return '—';
  if (item.member_id) {
    const name = `${item.member_nom || ''} ${item.member_prenom || ''}`.trim();
    if (item.member_type && item.member_type !== 'AGENT') return `${name} (ayant droit)`;
    return name || item.member_matricule || `Membre #${item.member_id}`;
  }
  const extName = `${item.external_nom || ''} ${item.external_prenom || ''}`.trim();
  return extName || `Externe #${item.external_member_id}`;
}

export function formatDoctorName(item) {
  if (!item) return '—';
  return `${item.doctor_first_name || ''} ${item.doctor_last_name || ''}`.trim() || '—';
}

export function formatSlotRange(item) {
  if (!item?.start_at) return '—';
  const datePart = formatAppDateTime(item.start_at, {
    weekday: 'short',
    day: '2-digit',
    month: 'short'
  });
  const timeStart = formatAppTime(item.start_at);
  if (!item.end_at) return `${datePart} · ${timeStart}`;
  return `${datePart} · ${timeStart}–${formatAppTime(item.end_at)}`;
}

export function formatSlotShort(item) {
  if (!item?.start_at) return '—';
  const timeStart = formatAppTime(item.start_at);
  if (!item.end_at) return timeStart;
  const timeEnd = formatAppTime(item.end_at);
  return `${timeStart}–${timeEnd}`;
}

export function getPatientInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function getAppointmentStatusPillProps(status, copy) {
  const cfg = APPOINTMENT_STATUS[status] || { label: status, modifier: 'absent' };
  return {
    label: copy?.[`STATUS_${status}`] || cfg.label || status,
    className: `hd-rdv-status-pill hd-rdv-status-pill--${cfg.modifier}`
  };
}

export function getAppointmentSlotPillProps(item) {
  const label = formatSlotShort(item);
  return {
    label,
    className: 'hd-rdv-slot-pill'
  };
}

export function isAppointmentUpcoming(item) {
  if (!item?.start_at) return false;
  if (item.status !== 'EN_ATTENTE' && item.status !== 'CONFIRME') return false;
  const start = parseToInstant(item.start_at);
  return start && start.getTime() >= Date.now();
}

export function buildAppointmentListSecondaryLine(item, copy) {
  const parts = [];
  if (item.reference) parts.push(item.reference);
  const doctor = formatDoctorName(item);
  if (doctor && doctor !== '—') parts.push(doctor);
  if (item.motif?.trim()) parts.push(item.motif.trim());
  if (item.priority && item.priority !== 'NORMALE') {
    parts.push(item.priority === 'URGENT' ? copy.PRIORITY_URGENT : item.priority);
  }
  return parts.join(' · ');
}

export const EMPTY_STATUS_BREAKDOWN = {
  all: 0,
  EN_ATTENTE: 0,
  CONFIRME: 0,
  TERMINE: 0,
  ANNULE: 0,
  ABSENT: 0
};

export function computeAppointmentStats(items) {
  const list = Array.isArray(items) ? items : [];
  return {
    count: list.length,
    pending: list.filter((i) => i.status === 'EN_ATTENTE').length,
    confirmed: list.filter((i) => i.status === 'CONFIRME').length,
    done: list.filter((i) => i.status === 'TERMINE').length
  };
}

export function computeAppointmentStatusBreakdown(items) {
  const list = Array.isArray(items) ? items : [];
  return {
    all: list.length,
    EN_ATTENTE: list.filter((i) => i.status === 'EN_ATTENTE').length,
    CONFIRME: list.filter((i) => i.status === 'CONFIRME').length,
    TERMINE: list.filter((i) => i.status === 'TERMINE').length,
    ANNULE: list.filter((i) => i.status === 'ANNULE').length,
    ABSENT: list.filter((i) => i.status === 'ABSENT').length
  };
}

export function getAppointmentStatusFilterProps(statusId, copy) {
  if (!statusId) {
    return { modifier: 'all', label: copy?.FILTER_STATUS_ALL || 'Tous' };
  }
  const cfg = APPOINTMENT_STATUS[statusId];
  return {
    modifier: cfg?.modifier || 'absent',
    label: copy?.[`STATUS_${statusId}`] || cfg?.label || statusId
  };
}

export { toDateTimeLocalValue };
